import React from "react";
import PropTypes from "prop-types";
import {
  LockClosedIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { COMPLIANCE_ALLOWED_ROLE_LABELS } from "hooks/useComplianceAccess";
import useComplianceAccess from "hooks/useComplianceAccess";

/**
 * Default fallback UI when user lacks compliance access
 */
const DefaultAccessDenied = () => (
  <div className="rounded-xl border border-rose-200/60 bg-gradient-to-br from-rose-50 to-rose-100/50 p-8 shadow-sm">
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
          <LockClosedIcon className="h-6 w-6 text-rose-600" />
        </div>
      </div>
      <div className="flex-1">
        <h3 className="text-base font-semibold text-rose-800">
          Access Restricted
        </h3>
        <p className="mt-1 text-sm text-rose-700/90 leading-relaxed">
          The compliance module is available to users with specific roles. Your
          current role does not have access to this feature.
        </p>
        <div className="mt-4 rounded-lg bg-white/60 p-4 border border-rose-100">
          <p className="text-xs font-medium text-rose-700/80 uppercase tracking-wide mb-2">
            Required Roles
          </p>
          <div className="flex flex-wrap gap-2">
            {COMPLIANCE_ALLOWED_ROLE_LABELS.map((role) => (
              <span
                key={role}
                className="inline-flex items-center rounded-md bg-rose-100/80 px-2.5 py-1 text-xs font-medium text-rose-700"
              >
                {role}
              </span>
            ))}
          </div>
        </div>
        <p className="mt-4 text-xs text-rose-600/80">
          If you need access, please contact your system administrator.
        </p>
      </div>
    </div>
  </div>
);

/**
 * API error fallback UI
 */
const ApiErrorFallback = ({ error, onRetry }) => (
  <div className="rounded-xl border border-amber-200/60 bg-gradient-to-br from-amber-50 to-amber-100/50 p-8 shadow-sm">
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
          <ExclamationCircleIcon className="h-6 w-6 text-amber-600" />
        </div>
      </div>
      <div className="flex-1">
        <h3 className="text-base font-semibold text-amber-800">
          Unable to Verify Access
        </h3>
        <p className="mt-1 text-sm text-amber-700/90 leading-relaxed">
          {error ||
            "We encountered an issue verifying your access permissions. Please try again."}
        </p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-4 inline-flex items-center rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-colors"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  </div>
);

ApiErrorFallback.propTypes = {
  error: PropTypes.string,
  onRetry: PropTypes.func,
};

/**
 * Default loading fallback UI
 */
const DefaultLoading = () => (
  <div className="flex flex-col items-center justify-center py-16">
    <SpinnerIcon className="h-8 w-8 text-sflPrimary animate-spin" />
    <p className="mt-3 text-sm text-gray-500">Verifying access...</p>
  </div>
);

/**
 * ComplianceAccessGate
 *
 * Wraps compliance content and only renders children if user has access.
 * Shows appropriate fallback UI for loading, denied, or error states.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render if access granted
 * @param {React.ReactNode} props.fallback - UI to show when access denied
 * @param {React.ReactNode} props.loadingFallback - UI to show while checking access
 * @param {boolean} props.allowAdminBypass - If true, always allow org/super admins
 * @param {string} props.apiError - Error message from API call
 * @param {Function} props.onRetry - Callback to retry on error
 */
const ComplianceAccessGate = ({
  children,
  fallback,
  loadingFallback,
  allowAdminBypass = false,
  apiError = null,
  onRetry = null,
}) => {
  const { canAccessCompliance, isAccessLoading, isOrgOrSuperAdmin } =
    useComplianceAccess();

  // Show loading state
  if (isAccessLoading) {
    return <>{loadingFallback || <DefaultLoading />}</>;
  }

  // Show API error state
  if (apiError) {
    return <ApiErrorFallback error={apiError} onRetry={onRetry} />;
  }

  // Check access
  if (!canAccessCompliance) {
    // Admin bypass option
    if (allowAdminBypass && isOrgOrSuperAdmin) {
      return <>{children}</>;
    }
    return <>{fallback || <DefaultAccessDenied />}</>;
  }

  return <>{children}</>;
};

ComplianceAccessGate.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  loadingFallback: PropTypes.node,
  allowAdminBypass: PropTypes.bool,
  apiError: PropTypes.string,
  onRetry: PropTypes.func,
};

export default ComplianceAccessGate;
